"use client";

import { useEffect, useRef, useState } from "react";

interface RevealOnScrollProps {
  children: React.ReactNode;
  /** Optional element-level delay in ms applied to the transition. */
  delay?: number;
  /** Pass-through className on the wrapper. */
  className?: string;
  /** Threshold passed to IntersectionObserver — fraction visible to trigger. */
  threshold?: number;
}

/**
 * Lightweight stand-in for framer-motion's `whileInView`. Wraps children in a
 * div that is hidden + translated until the IntersectionObserver fires, then
 * adds `is-visible`, which the .reveal class in globals.css transitions to
 * opacity:1 / translateY:0. Self-disconnects after the first fire so it does
 * not re-trigger on scroll-back.
 */
export function RevealOnScroll({
  children,
  delay = 0,
  className,
  threshold = 0.2,
}: RevealOnScrollProps) {
  const ref = useRef<HTMLDivElement>(null);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const node = ref.current;
    if (!node) return;
    const obs = new IntersectionObserver(
      (entries) => {
        if (entries[0]?.isIntersecting) {
          setVisible(true);
          obs.disconnect();
        }
      },
      { threshold },
    );
    obs.observe(node);
    return () => obs.disconnect();
  }, [threshold]);

  return (
    <div
      ref={ref}
      className={`reveal${visible ? " is-visible" : ""}${className ? " " + className : ""}`}
      style={delay ? { transitionDelay: `${delay}ms` } : undefined}
    >
      {children}
    </div>
  );
}
